import { useState } from "react";
import { useQuery } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { fetchProducts } from "../api/productService";
import { DEFAULT_PAGE_SIZE } from "../utils/constants";
import { usePermissions } from "./usePermissions";
import { toSortByParam } from "./useTableSort";
import { dateRangeCriteria } from "../utils/dateRangeFilters";
import { useListTotal } from "./useListTotal";
export var buildProductSearchCriteria = function (filters, publisher, publicOnlyView) {
    var _a;
    if (publicOnlyView === void 0) { publicOnlyView = false; }
    var search = {};
    var id = (_a = filters.id) === null || _a === void 0 ? void 0 : _a.trim();
    if (id) {
        search.id = id;
    }
    if (filters.name) {
        search.name = filters.name;
    }
    if (filters.contributor) {
        search.contributor = filters.contributor;
    }
    if (publisher === null || publisher === void 0 ? void 0 : publisher.id) {
        search.publisher = publisher.id;
    }
    if (filters.productNumber) {
        search.productNumber = filters.productNumber;
    }
    // The backend filters by a single accessibility value: exactly one checked option narrows
    // the search, both checked means all products (no accessibility criterion).
    // The public-only view (e.g. Distributor) sees PUBLIC products only, so a
    // (stale) accessibility selection is ignored instead of narrowing to nothing.
    if (!publicOnlyView && filters.accessibility.length === 1) {
        search.accessibility = filters
            .accessibility[0];
    }
    Object.assign(search, dateRangeCriteria(filters));
    if (!search.id && !search.name && !search.contributor && !search.publisher) {
        search.name = "";
    }
    return search;
};
export var useProductData = function (appliedFilters, buildQuery, selectedPublisher, pageSize, sort, enabled) {
    var _a;
    if (pageSize === void 0) { pageSize = DEFAULT_PAGE_SIZE; }
    if (enabled === void 0) { enabled = true; }
    var ky = useOkapiKy();
    var _b = useState(0), pageOffset = _b[0], setPageOffset = _b[1];
    var searchAsPublicOnly = usePermissions().searchAsPublicOnly;
    var searchCriteria = buildProductSearchCriteria(appliedFilters, selectedPublisher, searchAsPublicOnly);
    // the backend counts exactly only for the first page — keep that total while paging
    var _c = useListTotal(JSON.stringify(searchCriteria)), totalRecords = _c.totalRecords, recordPage = _c.recordPage;
    var resetPageOffset = function () { return setPageOffset(0); };
    var _d = useQuery(
    // Keyed by what is actually sent: a filter change that leaves the
    // request as it is must not search again.
    [
        "products",
        searchCriteria,
        pageOffset,
        pageSize,
        sort === null || sort === void 0 ? void 0 : sort.column,
        sort === null || sort === void 0 ? void 0 : sort.direction,
    ], function () {
        return fetchProducts(ky, {
            search: searchCriteria,
            offset: pageOffset,
            limit: pageSize,
            sortBy: sort ? toSortByParam(sort) : undefined,
        });
    }, {
        enabled: enabled,
        keepPreviousData: true,
        onSuccess: function (data) { var _a, _b; return recordPage(pageOffset, data.totalRecords, (_b = (_a = data.items) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0); },
    }), data = _d.data, isLoading = _d.isLoading, isError = _d.isError, refetch = _d.refetch;
    var productsData = (_a = data === null || data === void 0 ? void 0 : data.items) !== null && _a !== void 0 ? _a : [];
    var fetchMoreData = function (offset) {
        setPageOffset(offset);
    };
    return {
        productsData: productsData,
        totalRecords: totalRecords,
        isLoading: isLoading,
        /** true while the search waits for its filters (`enabled` false) — no request is running */
        isWaitingForFilters: !enabled,
        isError: isError,
        refetch: refetch,
        fetchMoreData: fetchMoreData,
        resetPageOffset: resetPageOffset,
        pageOffset: pageOffset,
        pageSize: pageSize,
    };
};
